import { useState, useRef, useEffect } from 'react';
import {
  EuiSuperDatePicker,
  EuiProvider,
  euiStylisPrefixer,
} from '@elastic/eui';
import { stringToMoment } from './flows/utils';
import { useGlobalSettings } from '../app/GlobalSettingProvider';
import moment from 'moment-timezone';
import { TimeZoneSelector } from './header/TimeZoneSelector';
import { Box, SxProps } from '@mui/material';
import { theme } from '../utils/theme';
import createCache from '@emotion/cache';

const cache = createCache({
  key: 'eui',
  stylisPlugins: [euiStylisPrefixer],
  container: document.querySelector('meta[name="eui-style-insert"]') as Node,
});

interface AQDatePickerProps {
  start?: string;
  end?: string;
  onTimeChange?: ({ start, end }: { start: string; end: string }) => void;
  hideTimeZoneSelector?: boolean;
  sx?: {
    container?: SxProps;
    datePicker?: any;
  };
}

export default function AQDatePicker({
  start,
  end,
  onTimeChange: externalOnTimeChange,
  hideTimeZoneSelector,
  sx,
}: AQDatePickerProps) {
  const [isPaused, setIsPaused] = useState(true);
  const { settings, setSettings } = useGlobalSettings();
  const [refreshInterval, setRefreshInterval] = useState(10000); // 10s

  // Helper function to check if a string is in UTC format
  const isUTCFormat = (dateString: string): boolean => {
    // Check if it's an ISO string (UTC format)
    if (moment(dateString, moment.ISO_8601, true).isValid()) {
      return true;
    }
    // Check if it's a relative time string (like 'now-1h')
    if (dateString.startsWith('now')) {
      return false;
    }
    // Check if it's a specific date string that can be parsed
    const parsed = moment(dateString);
    return parsed.isValid() && parsed.format('Z') === '+00:00';
  };

  // Helper function to convert date to UTC and adjust for timezone
  const convertToUTCAndAdjust = (dateString: string): string => {
    if (isUTCFormat(dateString)) {
      // Already in UTC, format properly for display
      return moment.utc(dateString).toISOString();
    } else {
      // Not in UTC, convert to UTC and adjust for timezone
      let momentObj: moment.Moment;

      if (dateString.startsWith('now')) {
        // Handle relative time strings
        momentObj = stringToMoment(dateString, 'start');
      } else {
        // Handle absolute date strings
        momentObj = moment(dateString);
      }

      if (!momentObj.isValid()) {
        throw new Error(`Invalid date string: ${dateString}`);
      }

      // Convert to UTC
      const utcMoment = momentObj.utc();

      // Adjust for the timezone offset in settings
      if (settings.timeZone.utcOffset !== 0) {
        // The utcOffset is the difference between local time and UTC
        // We need to subtract the offset to get the correct UTC time
        return utcMoment
          .subtract(settings.timeZone.utcOffset, 'minutes')
          .toISOString();
      }

      return utcMoment.toISOString();
    }
  };

  // Process start and end props if provided
  const processedStart = start
    ? convertToUTCAndAdjust(start)
    : settings.datePicker.start;
  const processedEnd = end
    ? convertToUTCAndAdjust(end)
    : settings.datePicker.end;

  const onTimeChange = ({ start, end }: { start: string; end: string }) => {
    const startMoment = stringToMoment(start, 'start');
    const endMoment = stringToMoment(end, 'end');

    if (!externalOnTimeChange) {
      // update settings.datePicker.start and settings.datePicker.end in UTC
      setSettings({
        ...settings,
        datePicker: {
          start: startMoment.utc().toISOString(),
          end: endMoment.utc().toISOString(),
        },
      });
    }

    // Call external callback if provided
    if (externalOnTimeChange) {
      externalOnTimeChange({
        start: startMoment.utc().toISOString(),
        end: endMoment.utc().toISOString(),
      });
    }
  };

  const onRefreshChange = ({
    isPaused,
    refreshInterval,
  }: {
    isPaused: boolean;
    refreshInterval: number;
  }) => {
    setIsPaused(isPaused);
    setRefreshInterval(refreshInterval);
  };

  // Helper to add utcOffset (in minutes) to an ISO string
  const addUtcOffset = (isoString: string, offsetMinutes: number) => {
    const timeWithOffset = moment
      .utc(isoString)
      .add(offsetMinutes, 'minutes')
      .toISOString();

    return timeWithOffset;
  };

  // Compute adjusted start/end for EuiSuperDatePicker
  const getPickerTime = (val: string) => {
    if (
      settings.timeZone.current === 'UTC' &&
      !val.startsWith('now') &&
      settings.timeZone.utcOffset !== 0
    ) {
      return addUtcOffset(val, settings.timeZone.utcOffset);
    }
    return val;
  };

  return (
    <EuiProvider cache={cache}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          marginRight: theme.spacing(4),
          ...sx?.container,
        }}
      >
        <EuiSuperDatePicker
          width="full"
          css={{ marginRight: theme.spacing(1), ...sx?.datePicker }}
          dateFormat={'MMM D, yyyy h:mm:ss a zzz'}
          start={getPickerTime(processedStart)}
          end={getPickerTime(processedEnd)}
          onTimeChange={onTimeChange}
          isPaused={isPaused}
          refreshInterval={refreshInterval}
          onRefreshChange={onRefreshChange}
          showUpdateButton={false}
          isAutoRefreshOnly={false}
          recentlyUsedRanges={[
            { start: 'now-15m', end: 'now' },
            { start: 'now-1h', end: 'now' },
            { start: 'now-24h', end: 'now' },
          ]}
        />
        {!hideTimeZoneSelector && <TimeZoneSelector />}
      </Box>
    </EuiProvider>
  );
}
